import { getCookieParam } from '~/utils/cookies';

import { UNICODE_VARIANTS } from '../constants/app';
import {
  ACCESS_TOKEN_COOKIE_NAME,
  FROM_IN_APP_BROWSER,
  USER_ORIGIN_COOKIE_NAME,
} from '../constants/auth';

import type { Request } from 'express';

/**
 * Format international phone number to local format
 * @param phoneNumber +614xxxxxxx
 * @param mask xxxx xxx xxx
 * @returns 04xxxxxxxx
 */
export const formatInternationalPhoneNumberTolocal = (
  phoneNumber?: string,
  mask?: string
): string => {
  if (!phoneNumber) {
    return '';
  }

  // Replace +61 with 0 if applicable
  let formatted = phoneNumber.startsWith('+61') ? phoneNumber.replace('+61', '0') : phoneNumber;
  // Remove non-digit characters
  formatted = formatted.replace(/\D/g, '');

  // Apply mask if specified
  if (mask === 'xxxx xxx xxx') {
    const match = formatted.match(/^(\d{0,4})(\d{0,3})(\d{0,3})$/);
    if (match) {
      formatted = [match[1], match[2], match[3]].filter(Boolean).join(' ');
    }
  }

  return formatted;
};

/**
 * Format local phone number to international format
 * @param phoneNumber 04xx xxx xxx
 * @returns +614xxxxxxx
 */
export const formatLocalPhoneNumberToInternational = (phoneNumber: string): string => {
  if (!phoneNumber.startsWith('0')) {
    return phoneNumber;
  }

  return phoneNumber.replace(/^0/, '+61').replace(/\s/g, '');
};

/**
 * This function is used to get the page url by replacing the params in the slug
 * @param slug "/test/[test]/test"
 * @param params { test: '123' }
 * @returns "/test/123/test"
 */
export const formatPageUrl = (slug: string, params: Record<string, string>): string => {
  let url = slug;

  Object.entries(params).forEach(([key, value]) => {
    url = url.replace(`[${key}]`, value);
  });

  return url;
};

/**
 * Check if the URL path matches the slug pattern
 * @param url The URL to check
 * @param slug The slug pattern to match against
 * @returns true if the URL path matches the slug pattern, false otherwise
 */
export const isPathMatchesSlug = (url: string, slug: string): boolean => {
  try {
    const cleanUrl = new URL(url).pathname; // Strip domain and query
    const urlParts = cleanUrl.split('/').filter(Boolean);
    const slugParts = slug.split('/').filter(Boolean);

    if (urlParts.length !== slugParts.length) {
      return false;
    }

    return slugParts.every((slugPart, index) => {
      const urlPart = urlParts[index];

      // Dynamic part: [param]
      if (slugPart.startsWith('[') && slugPart.endsWith(']')) {
        return true;
      }

      return slugPart === urlPart;
    });
  } catch (error) {
    return false;
  }
};

/**
 * Extracts the query string from the request URL.
 * @param req - Incoming HTTP request from context.req
 * @returns
 */
export const extractSSRQueryString = (req: Request): string => {
  const url = req.url || '';
  const queryString = url.split('?')[1] || '';

  return queryString;
};

export const extractClientQueryString = (): string => {
  if (typeof window !== 'undefined') {
    return window.location.search.substring(1); // removes the "?" at start
  }

  return '';
};

export const isNumberEven = (num: number): boolean => num % 2 === 0;

export const isAppOpenedInNativeAppBrowser = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const legacyAccessTokenOrigin = getCookieParam(ACCESS_TOKEN_COOKIE_NAME, 'origin');

  const accessTokenOrigin = getCookieParam(USER_ORIGIN_COOKIE_NAME);

  return (
    legacyAccessTokenOrigin === FROM_IN_APP_BROWSER || accessTokenOrigin === FROM_IN_APP_BROWSER
  );
};

export const isValidObject = (obj: Record<string, unknown>): boolean =>
  obj !== null && typeof obj === 'object' && !Array.isArray(obj);

export const isEmptyObject = (obj: Record<string, unknown>): boolean =>
  isValidObject(obj) && Object.keys(obj).length === 0;

/**
 * Loads image file, returns a promise that resolves to the HTMLImageElement.
 * @param file The image file to load.
 * @returns
 */
const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const imageUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = (): void => {
      URL.revokeObjectURL(imageUrl);
      resolve(image);
    };
    image.onerror = (): void => {
      URL.revokeObjectURL(imageUrl);
      reject(new Error('Failed to load image'));
    };

    image.src = imageUrl;
  });

/**
 * Converts a canvas element to a Blob.
 * @param canvas The canvas element to convert.
 * @param type The MIME type of the image (e.g., "image/jpeg").
 * @param quality The quality of the image (0 to 1).
 * @returns A promise that resolves to the image Blob.
 */
const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to create image blob'));
          return;
        }
        resolve(blob);
      },
      type,
      quality
    );
  });

/**
 * Compresses an image file to reduce its size and dimensions.
 * @param file The image file to compress.
 * @returns A promise that resolves to the compressed image file.
 */
export const compressImage = async ({
  file,
  maxCompressedSizeMB = 5,
  maxCompressedDimension = 1920,
}: {
  file: File;
  maxCompressedDimension?: number;
  maxCompressedSizeMB?: number;
}): Promise<File> => {
  const maxCompressedSizeBytes = maxCompressedSizeMB * 1024 * 1024;
  const image = await loadImage(file);
  const scale = Math.min(1, maxCompressedDimension / Math.max(image.width, image.height));
  const targetWidth = Math.round(image.width * scale);
  const targetHeight = Math.round(image.height * scale);
  const canvas = document.createElement('canvas');

  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Canvas context not available');
  }

  context.drawImage(image, 0, 0, targetWidth, targetHeight);

  const shouldKeepPng = file.type === 'image/png';
  let mimeType = shouldKeepPng ? 'image/png' : 'image/jpeg';
  let quality = 0.92;
  let blob = await canvasToBlob(canvas, mimeType, shouldKeepPng ? undefined : quality);

  if (blob.size > maxCompressedSizeBytes && shouldKeepPng) {
    mimeType = 'image/jpeg';
    quality = 0.9;
    blob = await canvasToBlob(canvas, mimeType, quality);
  }

  while (blob.size > maxCompressedSizeBytes && quality > 0.5) {
    quality -= 0.08;
    blob = await canvasToBlob(canvas, mimeType, quality);
  }

  return new File([blob], file.name, { type: blob.type });
};

export const normalizeText = (text: string): string =>
  [...text] // Handles Unicode surrogate pairs correctly
    .map((char) => UNICODE_VARIANTS[char] || char)
    .join('')
    .normalize('NFD') // Normalize diacritics
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritic marks
    .toLowerCase(); // Convert to lowercase
